import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { CowreMark, IconClose, IconPlus, IconTrash } from '../../../common/icons';
import { useOpuSession } from '../contexts/opu';
import { opuPaths } from '../queries/paths';
import { opuKeys } from '../queries/keys';
import { isEmptyRow, validateAspirations } from '../utils/validate';
import ClientFarmSearch from '../../clients/forms/search';
import UserSelect from '../../users/forms/search';
import AspirationRows from '../forms/aspiration-rows';
import ReorganizeModal from '../../clivages/forms/reorganize-modal';

import './styles/session-workspace.css';
import './styles/edit-workspace.css';

const ROLE_OPTIONS = [
    { value: 'lead_vet', label: 'Aspirador' },
    { value: 'assistant', label: 'Asistente' },
];
const ROLE_LABEL = { lead_vet: 'Aspirador', assistant: 'Asistente', operator: 'Operario' };
const today = () => new Date().toLocaleDateString('en-CA');

export default function OpuEditWorkspace() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [params] = useSearchParams();
    const sessionId = params.get('session');

    const { data: session, isLoading, error } = useOpuSession(sessionId);

    const [farm, setFarm] = useState(null);
    const [date, setDate] = useState('');
    const [staff, setStaff] = useState([]);
    const [staffUser, setStaffUser] = useState(null);
    const [staffRole, setStaffRole] = useState('lead_vet');
    const [userKey, setUserKey] = useState(0);
    const [rows, setRows] = useState([]);
    const [aspErrors, setAspErrors] = useState({});
    const [seq, setSeq] = useState(1);
    const [seeded, setSeeded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showReorg, setShowReorg] = useState(false);

    // seed from the assembled session (adjust-during-render)
    if (session && !seeded) {
        setFarm(session.farm ? { id: session.farm.id, name: session.farm.name, owner: session.farm.owner || null } : null);
        setDate(session.date || '');
        setStaff((session.staff || []).filter(s => s.status !== 0).map((s, i) => ({
            key: `st${i}`, staffId: s.id, user: s.user, role: s.role,
        })));
        setRows((session.aspirations || []).filter(a => a.status !== 0).map((a, i) => ({
            key: `a${i}`,
            aspId: a.id,
            donor: a.donor ? { id: a.donor.id, name: a.donor.name, number: a.donor.number } : null,
            total: String(a.total ?? ''),
            viables: String(a.viables ?? ''),
            notes: typeof a.remarks === 'string' ? a.remarks : '',
        })));
        setSeeded(true);
    }

    if (isLoading || (!session && !error)) return <div className="opu-ws__state">Cargando sesión…</div>;
    if (error || !session) return <div className="opu-ws__state">Sesión no encontrada.</div>;

    // ---- aspirations grid handlers (mirror create, with active viables guard) ----
    const clearRowErr = (key, field) => setAspErrors(prev => (prev[key] ? { ...prev, [key]: { ...prev[key], [field]: undefined } } : prev));
    const addRow = () => { setRows(prev => [...prev, { key: `n${seq}`, aspId: null, donor: null, total: '', viables: '', notes: '' }]); setSeq(s => s + 1); };
    const removeRow = (key) => {
        if (rows.length === 1) {
            setRows([{ key: `n${seq}`, aspId: null, donor: null, total: '', viables: '', notes: '' }]);
            setSeq(s => s + 1); // bump so the fresh row's key is never reused
            return;
        }
        setRows(prev => prev.filter(r => r.key !== key));
    };
    const setDonor = (key, donor) => { setRows(prev => prev.map(r => (r.key === key ? { ...r, donor } : r))); clearRowErr(key, 'donor'); };
    const setRowField = (key, field, value) => {
        if (field === 'viables' && value !== '') {
            const row = rows.find(r => r.key === key);
            if (row && row.total !== '' && Number(value) > Number(row.total)) {
                toast.error('Los viables no pueden superar los óvocitos totales');
                return;
            }
        }
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
        clearRowErr(key, field);
    };

    // ---- staff handlers ----
    const addStaff = () => {
        if (!staffUser) { toast.error('Elegí un usuario'); return; }
        if (staff.some(s => s.user.id === staffUser.id && s.role === staffRole)) { toast.error('Ese usuario ya tiene ese rol'); return; }
        setStaff(prev => [...prev, { key: `s${seq}`, staffId: null, user: staffUser, role: staffRole }]);
        setSeq(s => s + 1);
        setStaffUser(null);
        setUserKey(k => k + 1);
    };
    const removeStaff = (key) => setStaff(prev => prev.filter(s => s.key !== key));

    const applyOrder = (keys) => {
        setRows(prev => { const byKey = Object.fromEntries(prev.map(r => [r.key, r])); return keys.map(k => byKey[k]).filter(Boolean); });
        setShowReorg(false);
    };

    const handleSave = async () => {
        if (!farm) { toast.error('La finca es obligatoria'); return; }
        const { rowErrors, valid, considered } = validateAspirations(rows);
        if (!valid) { setAspErrors(rowErrors); toast.error(considered.length ? 'Revisá las aspiraciones' : 'Agregá al menos una donante'); return; }
        setAspErrors({});

        setSaving(true);
        const failures = [];
        let attempted = 0;
        const run = async (label, fn) => { attempted++; try { await fn(); } catch { failures.push(label); } };

        // header
        const headerChanges = {};
        if (farm.id !== session.farm?.id) headerChanges.farm_id = farm.id;
        if (date && date !== session.date) headerChanges.date = date;
        if (Object.keys(headerChanges).length) await run('cabecera', () => api.patch(opuPaths.detail(sessionId), headerChanges));

        // staff diff — delete removed, then add new
        const loadedStaff = (session.staff || []).filter(s => s.status !== 0);
        for (const ls of loadedStaff) {
            if (!staff.some(s => s.staffId === ls.id)) await run(`staff ${ls.user?.name || ''}`, () => api.delete(opuPaths.staffItem(sessionId, ls.id)));
        }
        for (const s of staff) {
            if (!s.staffId) await run(`staff ${s.user?.name || ''}`, () => api.post(opuPaths.staff(sessionId), { user_id: s.user.id, role: s.role }));
        }

        // aspirations diff — DELETE removed FIRST so re-adding the same donor
        // doesn't 409 on the unique (session, donor) guard (the deleted one is
        // soft-deleted; the unique index is partial on status<>0).
        const loadedAsp = (session.aspirations || []).filter(a => a.status !== 0);
        for (const la of loadedAsp) {
            if (!considered.some(r => r.aspId === la.id)) await run(`borrar ${la.donor?.name || ''}`, () => api.delete(opuPaths.aspiration(sessionId, la.id)));
        }
        for (const r of considered) {
            const base = { donor_id: r.donor.id, total: Number(r.total), viables: Number(r.viables) };
            if (r.aspId) {
                const orig = loadedAsp.find(a => a.id === r.aspId);
                const changed = !orig || Number(r.total) !== orig.total || Number(r.viables) !== orig.viables ||
                    r.donor.id !== orig.donor?.id || (typeof orig.remarks === 'string' ? orig.remarks : '') !== (r.notes || '');
                // remarks is accepted on PATCH aspiration but NOT on the POST body.
                if (changed) await run(`aspiración ${r.donor?.name || ''}`, () => api.patch(opuPaths.aspiration(sessionId, r.aspId), { ...base, remarks: r.notes?.trim() ? r.notes.trim() : null }));
            } else {
                await run(`aspiración ${r.donor?.name || ''}`, () => api.post(opuPaths.aspirations(sessionId), base));
            }
        }

        setSaving(false);
        qc.invalidateQueries({ queryKey: opuKeys.all });

        // every attempted op failed → keep the user on the page (don't lose edits)
        if (attempted > 0 && failures.length === attempted) {
            toast.error(`No se pudo guardar: ${failures.join(' · ')}`);
            return;
        }
        if (failures.length) toast.error(`Guardado con avisos: ${failures.join(' · ')}`);
        else toast.success('Sesión actualizada');
        navigate(`/admin/opu/${sessionId}`);
    };

    return (
        <div className="opu-ws">
            <header className="opu-ws__topbar">
                <div className="opu-ws__brand">
                    <span className="opu-ws__mark"><CowreMark size={30} id="opu-edit-mark" /></span>
                    <span className="opu-ws__brand-text">COWRE</span>
                    <span className="opu-ws__divider" />
                    <span className="opu-ws__title">Editar OPU · {session.code}</span>
                </div>
                <button type="button" className="opu-ws__close" onClick={() => navigate(`/admin/opu/${sessionId}`)} aria-label="Cerrar"><IconClose /></button>
            </header>

            <div className="opu-ws__content">
                <section className="opu-edit__card">
                    <div className="opu-edit__row">
                        <div className="opu-edit__field opu-edit__field--farm">
                            <span className="opu-edit__label">Finca</span>
                            {farm ? (
                                <div className="opu-edit__chip">
                                    <span>{farm.name}{farm.owner?.name ? ` · ${farm.owner.name}` : ''}</span>
                                    <button type="button" onClick={() => setFarm(null)} aria-label="Cambiar finca"><IconClose /></button>
                                </div>
                            ) : (
                                <ClientFarmSearch farmsOnly onSelect={(item) => setFarm({ id: item.id, name: item.name, owner: item.owner || null })} placeholder="Buscar finca o dueño…" />
                            )}
                        </div>
                        <div className="opu-edit__field">
                            <span className="opu-edit__label">Fecha</span>
                            <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
                        </div>
                    </div>

                    <div className="opu-edit__field">
                        <span className="opu-edit__label">Equipo</span>
                        <div className="opu-edit__staff-add">
                            <UserSelect key={userKey} onSelect={setStaffUser} placeholder="Buscar usuario…" />
                            <select value={staffRole} onChange={(e) => setStaffRole(e.target.value)}>
                                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <button type="button" className="opu-edit__add-btn" onClick={addStaff}><IconPlus /><span>Agregar</span></button>
                        </div>
                        {staff.length > 0 && (
                            <ul className="opu-edit__staff-list">
                                {staff.map(s => (
                                    <li key={s.key}>
                                        <span className="opu-edit__staff-name">{s.user?.name}</span>
                                        <span className="opu-edit__staff-role">{ROLE_LABEL[s.role] || s.role}</span>
                                        <button type="button" onClick={() => removeStaff(s.key)} aria-label="Quitar"><IconTrash /></button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <div className="opu-edit__section-head">
                    <h2 className="opu-edit__section-title">Aspiraciones</h2>
                    <button type="button" className="opu-edit__reorg" onClick={() => setShowReorg(true)} disabled={saving || rows.length < 2}>Reorganizar</button>
                </div>
                <AspirationRows
                    rows={rows}
                    errors={aspErrors}
                    onAdd={addRow}
                    onField={setRowField}
                    onDonor={setDonor}
                    onClearDonor={(key) => setDonor(key, null)}
                    onRemove={removeRow}
                    disabled={saving}
                />
            </div>

            <footer className="opu-ws__footer">
                <span className="opu-ws__footer-hint">{considered(rows)} aspiración(es)</span>
                <div className="opu-ws__actions">
                    <button type="button" className="opu-ws__cancel" onClick={() => navigate(`/admin/opu/${sessionId}`)} disabled={saving}>Cancelar</button>
                    <button type="button" className="opu-ws__primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            </footer>

            {showReorg && (
                <ReorganizeModal
                    items={rows.map(r => ({ id: r.key, label: r.donor?.name ? `${r.donor.name}${r.donor.number ? ` · ${r.donor.number}` : ''}` : 'Nueva fila' }))}
                    onApply={applyOrder}
                    onClose={() => setShowReorg(false)}
                />
            )}
        </div>
    );
}

function considered(rows) {
    return rows.filter(r => !isEmptyRow(r)).length;
}
